<template>
  <div class="input-wrapper">
    <label v-if="label" :for="inputId" class="block text-sm font-medium text-text capitalize text-left mb-1.5">
      {{ label }}
      <span v-if="required" class="text-error ml-1">*</span>
    </label>
    
    <div class="relative">
      <div v-if="$slots.prefix" class="absolute left-3 top-1/2 -translate-y-1/2 z-10">
        <slot name="prefix" />
      </div>
      <input
        :id="inputId"
        :type="type"
        :name="name"
        :value="modelValue"
        :placeholder="resolvedPlaceholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :aria-label="props['aria-label']"
        :aria-describedby="describedBy"
        :autocomplete="autocomplete"
        :min="min"
        :max="max"
        :step="step"
        :minlength="minlength"
        :maxlength="maxlength"
        :pattern="pattern"
        :inputmode="inputmode"
        :class="[
          'input-field',
          `state-${state}`,
          $slots.prefix ? '!pl-10 pr-3' : 'px-3',
          $slots.suffix ? '!pr-12' : ''
        ]"
        @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
        @keydown="$emit('keydown', $event)"
        @keyup="$emit('keyup', $event)"
        @paste="$emit('paste', $event)"
      />
      <div v-if="$slots.suffix" class="absolute right-3 top-1/2 -translate-y-1/2 z-10 flex items-center gap-1">
        <slot name="suffix" />
      </div>
    </div>
    
    <div v-if="helpText && !errorText" class="mt-1 text-sm text-text-muted">
      {{ helpText }}
    </div>
    
    <div v-if="errorText" class="mt-1 text-sm text-error">
      {{ errorText }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { WEEKLY_CHECK_IN_SUPPRESS_PLACEHOLDERS } from '@/constants/weeklyCheckInPrintContext'

interface Props {
  modelValue?: string | number | null | null
  type?: 'text' | 'email' | 'password' | 'tel' | 'url' | 'search' | 'number' | 'date'
  state?: 'default' | 'success' | 'warning' | 'danger'
  disabled?: boolean
  readonly?: boolean
  required?: boolean
  placeholder?: string
  label?: string
  helpText?: string
  errorText?: string
  'aria-label'?: string
  'aria-describedby'?: string
  autocomplete?: string
  /** For `type="date"`, use ISO `YYYY-MM-DD` strings; for `number`, use numeric bounds. */
  min?: number | string
  max?: number | string
  minlength?: number
  maxlength?: number
  pattern?: string
  step?: number | string
  name?: string
  id?: string
  inputmode?: 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url'
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  state: 'default',
  disabled: false,
  readonly: false,
  required: false
})

const suppressPrintPlaceholders = inject(WEEKLY_CHECK_IN_SUPPRESS_PLACEHOLDERS, false)

const resolvedPlaceholder = computed(() =>
  suppressPrintPlaceholders ? undefined : props.placeholder
)

defineEmits<{
  'update:modelValue': [value: string | number | null]
  focus: [event: FocusEvent]
  blur: [event: FocusEvent]
  keydown: [event: KeyboardEvent]
  keyup: [event: KeyboardEvent]
  paste: [event: ClipboardEvent]
}>()

// Generate unique ID for accessibility (use provided id prop if available)
const inputId = computed(() => props.id || `input-${Math.random().toString(36).substr(2, 9)}`)

// Computed aria-describedby for accessibility
const describedBy = computed(() => {
  const ids = []
  if (props.helpText && !props.errorText) ids.push(`${inputId.value}-help`)
  if (props.errorText) ids.push(`${inputId.value}-error`)
  return ids.length > 0 ? ids.join(' ') : undefined
})
</script>

<style scoped>
.input-field {
  width: 100%;
  padding: 0.625rem 0.75rem; /* py-2.5 px-3 - match side panel / design */
  font-size: 0.875rem;
  line-height: 1.25rem;
  border: 1px solid;
  border-radius: 0.5rem;
  box-sizing: border-box;
  /* Use theme variables (unified with panels everywhere) */
  background-color: var(--color-input-background);
  border-color: var(--color-input-border);
  color: var(--color-input-text);
}

.input-field:focus {
  outline: none;
  border-color: var(--color-input-focus);
  box-shadow: 0 0 0 2px var(--color-input-focus-ring);
}

.input-field:disabled {
  background-color: var(--color-secondary);
  color: var(--color-text-muted);
  cursor: not-allowed;
}

.input-field:read-only {
  background-color: var(--color-secondary);
  cursor: default;
}

.input-field::placeholder {
  color: var(--color-input-placeholder);
}

/* Hide native browser search clear button - we use custom clear buttons via suffix slot */
.input-field[type="search"]::-webkit-search-cancel-button {
  -webkit-appearance: none;
  appearance: none;
  display: none;
}

/* Hide native clear button for IE/Edge */
.input-field[type="search"]::-ms-clear {
  display: none;
}

/* State variants */
.input-field.state-success {
  border-color: var(--color-success);
}

.input-field.state-success:focus {
  border-color: var(--color-success);
  box-shadow: 0 0 0 2px var(--color-success);
}

.input-field.state-warning {
  border-color: var(--color-warning);
}

.input-field.state-warning:focus {
  border-color: var(--color-warning);
  box-shadow: 0 0 0 2px var(--color-warning);
}

.input-field.state-danger {
  border-color: var(--color-danger);
}

.input-field.state-danger:focus {
  border-color: var(--color-danger);
  box-shadow: 0 0 0 2px var(--color-danger);
}
</style>
